/**
 * IAModeledBanner Component
 * Shows a banner when modeled IA score is enabled, allowing users to toggle it off
 * Design matches PIModeledBanner for consistency
 */

import { useState } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { AlertTriangle, AlertCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface IAModeledBannerProps {
  useModeledIa: boolean;
  modeledIaPercent: number;
  onToggleModeled: (useModeled: boolean) => void;
  isUpdating?: boolean;
  /** When true (e.g. locked year), the modeled/calculator toggle is disabled. */
  disabled?: boolean;
}

export function IAModeledBanner({
  useModeledIa,
  modeledIaPercent,
  onToggleModeled,
  isUpdating = false,
  disabled = false,
}: IAModeledBannerProps) {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const handleToggleClick = () => {
    if (useModeledIa) {
      // Switching from modeled to calculator - show confirmation
      setShowConfirmDialog(true);
    } else {
      // Switching from calculator to modeled - direct toggle
      onToggleModeled(true);
    }
  };

  const handleConfirmSwitch = () => {
    onToggleModeled(false);
    setShowConfirmDialog(false);
  };

  if (!useModeledIa) {
  return (
      <Alert className="border-[#4a6a8f]/30 bg-[#5a7a9f]/5">
        <AlertCircle className="h-4 w-4 text-[#5a7a9f]" />
        <AlertTitle className="text-[#4a6a8f]">IA Calculator Active</AlertTitle>
        <AlertDescription className="flex items-center justify-between">
          <div className="text-[#5a7a9f]">
            <p>
              Your IA score is currently being calculated from the activity attestations below.
            </p>
            <p className="text-sm mt-1">
              To use a modeled value instead, switch using the toggle.
            </p>
          </div>
          <div className="flex items-center gap-4 ml-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-[#5a7a9f]">Modeled</span>
              <Switch
                checked={!useModeledIa}
                onCheckedChange={() => handleToggleClick()}
                disabled={isUpdating || disabled}
              />
              <span className="text-sm text-[#5a7a9f]">Calculator</span>
            </div>
            </div>
        </AlertDescription>
      </Alert>
    );
  }
          
  return (
    <>
      <Alert className="border-amber-500/30 bg-amber-500/5">
        <AlertTriangle className="h-4 w-4 text-amber-600" />
        <AlertTitle className="text-amber-700">Modeled IA Score Active</AlertTitle>
        <AlertDescription className="flex items-center justify-between">
          <div className="text-amber-600">
            <p>
              Your IA score is currently set to a modeled value of{' '}
              <span className="font-semibold">{modeledIaPercent}%</span> (configured in Settings).
            </p>
            <p className="text-sm mt-1">
              To use actual IA data, switch to the IA Calculator below.
            </p>
          </div>
          <div className="flex items-center gap-4 ml-4">
          <div className="flex items-center gap-2">
              <span className="text-sm text-amber-600">Modeled</span>
              <Switch
                checked={!useModeledIa}
                onCheckedChange={() => handleToggleClick()}
                disabled={isUpdating || disabled}
              />
              <span className="text-sm text-amber-600">Calculator</span>
          </div>
        </div>
        </AlertDescription>
      </Alert>

      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Switch to IA Calculator?</DialogTitle>
            <DialogDescription>
              You are about to switch from using a modeled IA score to the IA Calculator.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              This will:
            </p>
            <ul className="list-disc list-inside text-sm text-muted-foreground mt-2 space-y-1">
              <li>Replace your modeled IA score ({modeledIaPercent}%) with calculated values</li>
              <li>Require you to attest to actual improvement activities</li>
              <li>Update your MIPS score based on the calculated IA score</li>
            </ul>
            <p className="text-sm text-muted-foreground mt-4">
              You can switch back to modeled scoring at any time from this page or the Configuration page.
        </p>
    </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleConfirmSwitch} disabled={isUpdating}>
              Switch to Calculator
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
